import { API_BASE_URL } from '$lib/config';

// Access token lives in memory only; the refresh token is an httpOnly cookie
// set by the server. Nothing sensitive touches localStorage.
let accessToken: string | null = null;
let refreshPromise: Promise<{ access: string }> | null = null;

export function getAccessToken(): string | null {
  return accessToken;
}

export function setAccessToken(token: string | null): void {
  accessToken = token;
}

export type LoginChallenge = {
  status: 'mfa_required' | 'mfa_setup_required';
  mfa_token: string;
};

export type TwoFASetup = {
  secret: string;
  otpauth_url: string;
  qr: string;
};

export type AuthUser = {
  id: number;
  email: string;
  name: string;
  role: 'admin' | 'member';
};

export type AuthSuccess = {
  access: string;
  expires_in: number;
  user: AuthUser;
  recovery_codes?: string[];
};

async function postJSON<T>(path: string, body: unknown): Promise<T> {
  const response = await fetch(`${API_BASE_URL}${path}`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body)
  });
  const data: unknown = await response.json().catch(() => ({}));
  if (!response.ok) {
    const detail =
      typeof data === 'object' && data !== null && 'detail' in data
        ? String((data as { detail?: unknown }).detail)
        : null;
    throw new Error(detail || 'Request failed');
  }
  return data as T;
}

function finalizeAuth(data: AuthSuccess) {
  setAccessToken(data.access);
}

// login performs the password step. 2FA is mandatory, so it never returns
// tokens directly — it returns a challenge to verify a code or enroll.
export async function login(email: string, password: string): Promise<LoginChallenge> {
  return postJSON<LoginChallenge>('/auth/login', { email, password });
}

export async function loginVerify(mfaToken: string, code: string): Promise<AuthSuccess> {
  const data = await postJSON<AuthSuccess>('/auth/login/verify', { mfa_token: mfaToken, code });
  finalizeAuth(data);
  return data;
}

export async function twoFASetup(mfaToken: string): Promise<TwoFASetup> {
  return postJSON<TwoFASetup>('/auth/2fa/setup', { mfa_token: mfaToken });
}

export async function twoFAEnable(mfaToken: string, code: string): Promise<AuthSuccess> {
  const data = await postJSON<AuthSuccess>('/auth/2fa/enable', { mfa_token: mfaToken, code });
  finalizeAuth(data);
  return data;
}

export async function refreshSession(): Promise<{ access: string }> {
  if (refreshPromise) return refreshPromise;
  refreshPromise = (async () => {
    const response = await fetch(`${API_BASE_URL}/auth/refresh`, {
      method: 'POST',
      credentials: 'include'
    });
    const data: unknown = await response.json().catch(() => ({}));
    if (!response.ok) {
      setAccessToken(null);
      const detail =
        typeof data === 'object' && data !== null && 'detail' in data
          ? String((data as { detail?: unknown }).detail)
          : null;
      throw new Error(detail || 'Session expired');
    }
    const access = (data as { access: string }).access;
    setAccessToken(access);
    return { access };
  })();
  try {
    return await refreshPromise;
  } finally {
    refreshPromise = null;
  }
}

export async function logout(): Promise<void> {
  try {
    await fetch(`${API_BASE_URL}/auth/logout`, { method: 'POST', credentials: 'include' });
  } finally {
    setAccessToken(null);
  }
}

export async function ensureSession(): Promise<boolean> {
  if (accessToken) return true;
  try {
    await refreshSession();
    return true;
  } catch {
    return false;
  }
}
